import * as THREE from 'three';
import { TERRAIN, WATER } from '../config';
import { terrainHeight } from './terrain';

// SHORE SURGE: one slow wave phase (0..1) shared by the foam sheet AND the terrain's wet-sand band, so
// the foam runs UP the beach and the sand it just touched darkens, then dries as the surge pulls back.
// Pure function of world XZ + time -> both shaders stay in phase for free. Self-contained (carries its
// own fp32-safe noise, oxSrg-prefixed) so every consumer gets identical code with no name collisions.
export const shoreSurgeGLSL = /* glsl */ `
float oxSrgHash(vec2 p){ p = mod(p, 1024.0); vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973)); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
float oxSrgNoise(vec2 p){ vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f); float a = oxSrgHash(i), b = oxSrgHash(i + vec2(1.0, 0.0)), c = oxSrgHash(i + vec2(0.0, 1.0)), e = oxSrgHash(i + vec2(1.0, 1.0)); return mix(mix(a, b, f.x), mix(c, e, f.x), f.y); }
float oxShoreSurge(vec2 wxz, float t){
  float ph = wxz.x * 0.021 + wxz.y * 0.017;              // along-shore variation so the surge isn't one flat line
  ph += (oxSrgNoise(wxz * 0.013) - 0.5) * 5.0;           // ~75m patches beat on their own clock, so a whole lake never washes at once
  return 0.5 + 0.5 * sin(t * 0.55 + ph + sin(t * 0.23 + ph * 1.7) * 0.8); // slow breath with an irregular beat
}`;

// OCEAN SWELL: the 3D wave height field, shared by the WATER sheet and this FOAM sheet -- both displace
// their vertices by the SAME function, so the foam always rides the moving surface. Shore-side the swell
// is EXACTLY the foam's front-A phase (oxFrontA), so the white crest lines sit on the geometric crests;
// deep-side it is two long directional trains with light interference. blendK keeps shallow lakes on the
// gentle shore-lap and gives the open sea the full trains. amp is the live WATER.swellAmp (F2 knob).
export const swellGLSL = /* glsl */ `
float oxSwlHash(vec2 p){ p = mod(p, 1024.0); vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973)); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
float oxSwlNoise(vec2 p){ vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f); float a = oxSwlHash(i), b = oxSwlHash(i + vec2(1.0, 0.0)), c = oxSwlHash(i + vec2(0.0, 1.0)), e = oxSwlHash(i + vec2(1.0, 1.0)); return mix(mix(a, b, f.x), mix(c, e, f.x), f.y); }
float oxWobble(vec2 wxz, float t){ return sin(wxz.x * 0.045 + wxz.y * 0.035 + t * 0.7) * 0.7; }
float oxDepthWarp(float d){ return d - 1.4 * smoothstep(0.2, 3.0, d); }
float oxFrontA(vec2 wxz, float d, float t){ return fract(oxDepthWarp(d) * 0.26 + t * 0.115 + oxWobble(wxz, t) * 0.055); }
float oxFrontB(vec2 wxz, float d, float t){ return fract(oxDepthWarp(d) * 0.36 + t * 0.145 + oxWobble(wxz, t) * 0.040 + 0.43); }
float oxSwell(vec2 wxz, float d, float t, float amp, out vec2 grad){
  vec2 k1 = vec2(0.85, 0.53) * 0.241;   // ~26m wave train
  vec2 k2 = vec2(-0.31, 0.95) * 0.334;  // ~19m train, off-angle for interference
  // SETS + WANDER: each train's amplitude breathes through slow drifting patches (waves arrive in
  // groups, with lulls) and a low-freq phase wander bends the crest lines -- so the deep sea reads
  // as shifting random swells, never a repeating corduroy grid.
  float wander = (oxSwlNoise(wxz * 0.011 + vec2(t * 0.013, -t * 0.009)) - 0.5) * 3.2;
  float setA = 0.35 + 0.85 * oxSwlNoise(wxz * 0.006 + vec2(t * 0.016, t * 0.011));
  float setB = 0.35 + 0.85 * oxSwlNoise(wxz * 0.0075 + vec2(-t * 0.012, t * 0.018) + 47.0);
  float p1 = dot(wxz, k1) + t * 0.55 + wander;
  float p2 = dot(wxz, k2) + t * 0.74 - wander * 0.7;
  float deep = sin(p1) * 0.7 * setA + sin(p2 + sin(p1 * 0.5) * 0.6) * 0.45 * setB;
  float shore = cos(oxFrontA(wxz, d, t) * 6.2831853) * smoothstep(0.25, 1.2, d) * 0.9;
  float blendK = smoothstep(2.0, 6.0, d);
  grad = (cos(p1) * k1 * 0.7 * setA + cos(p2) * k2 * 0.45 * setB) * amp * blendK;
  return amp * mix(shore, deep, blendK);
}`;

// Two-pass 8-way chamfer relax over a gp x gp grid: cells pre-seeded 0 spread their distance outward
// (orthogonal cost = step, diagonal = step*sqrt2). Shared by the foam + water sheets' land / open-water
// detectors; call with the array pre-filled 0 (seed) / 1e9 (far).
export function chamferRelax(sd: Float32Array, gp: number, step: number): void {
  const gw = gp - 1, DIAG = step * Math.SQRT2;
  for (let j = 0; j < gp; j++) for (let i = 0; i < gp; i++) {
    const b = j * gp + i; let v = sd[b];
    if (i > 0 && sd[b - 1] + step < v) v = sd[b - 1] + step;
    if (j > 0) {
      if (sd[b - gp] + step < v) v = sd[b - gp] + step;
      if (i > 0 && sd[b - gp - 1] + DIAG < v) v = sd[b - gp - 1] + DIAG;
      if (i < gw && sd[b - gp + 1] + DIAG < v) v = sd[b - gp + 1] + DIAG;
    }
    sd[b] = v;
  }
  for (let j = gp - 1; j >= 0; j--) for (let i = gp - 1; i >= 0; i--) {
    const b = j * gp + i; let v = sd[b];
    if (i < gw && sd[b + 1] + step < v) v = sd[b + 1] + step;
    if (j < gp - 1) {
      if (sd[b + gp] + step < v) v = sd[b + gp] + step;
      if (i < gw && sd[b + gp + 1] + DIAG < v) v = sd[b + gp + 1] + DIAG;
      if (i > 0 && sd[b + gp - 1] + DIAG < v) v = sd[b + gp - 1] + DIAG;
    }
    sd[b] = v;
  }
}

// Animated shoreline FOAM as a SEPARATE follow-plane floating just over the water (the water shader stays
// untouched). A per-vertex seabed sample gives each fragment its water depth; the beach side of the sheet
// DRAPES over the rising sand (a flat sheet buries the swash under the terrain), discrete wave fronts roll
// shoreward, and every bit of alpha is thresholded against scrolling noise so foam reads as torn white lace,
// not an airbrushed gradient. Mirrors the water's world-anchored grid seabed so it never swims as you walk.

// Foam-local fp32-safe value noise (Hoskins hash on a wrapped domain -- same recipe as the terrain's gHash,
// renamed so injecting it never collides with another shader's copy).
const FOAM_NOISE_GLSL = /* glsl */ `
float oxFmHash(vec2 p){ p = mod(p, 1024.0); vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973)); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
float oxFmNoise(vec2 p){ vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f); float a = oxFmHash(i), b = oxFmHash(i + vec2(1.0, 0.0)), c = oxFmHash(i + vec2(0.0, 1.0)), e = oxFmHash(i + vec2(1.0, 1.0)); return mix(mix(a, b, f.x), mix(c, e, f.x), f.y); }`;

const FOAM_SIZE = 380;           // follow-plane extent (foam only lives near shore, so smaller than the water sheet)
const FOAM_SEGS = 96;            // vertex resolution across the plane
const GRID_STEP = 8;             // terrain sample spacing (m)
const GP = Math.ceil(FOAM_SIZE / GRID_STEP) + 3; // grid points per side (+margin)
const GW = GP - 1;
const SHORE_MAX = 60;            // shore-distance clamp (m); the shader fades shore foam well inside this

export class FoamLayer {
  readonly mesh: THREE.Mesh;
  private readonly seabed: THREE.BufferAttribute;
  private readonly shoreAttr: THREE.BufferAttribute;
  private readonly localX: Float32Array;
  private readonly localZ: Float32Array;
  private readonly hm = new Float64Array(GP * GP);
  private readonly sd = new Float32Array(GP * GP); // chamfer distance-to-dry-land per grid point (m)
  private readonly od = new Float32Array(GP * GP); // chamfer distance-to-OPEN-water (lake/river = maxed = calm)
  private readonly openAttr: THREE.BufferAttribute;
  private gx0 = NaN; private gz0 = NaN; private lastX = NaN; private lastZ = NaN;
  private readonly uTime: { value: number };
  private readonly uSea = { value: TERRAIN.seaLevel };
  private readonly uStr = { value: 1.0 };
  private readonly uSwell = { value: WATER.swellAmp }; // live 3D-wave height (must match the water sheet)
  private readonly uDay = { value: 1 }; // 0 night .. 1 day: dims the bright foam so it doesn't glow white after dark

  constructor(uTime: { value: number }) {
    this.uTime = uTime;
    const geom = new THREE.PlaneGeometry(FOAM_SIZE, FOAM_SIZE, FOAM_SEGS, FOAM_SEGS);
    geom.rotateX(-Math.PI / 2);
    const n = geom.attributes.position.count;
    this.seabed = new THREE.BufferAttribute(new Float32Array(n).fill(TERRAIN.seaLevel - 5), 1);
    this.seabed.setUsage(THREE.DynamicDrawUsage);
    geom.setAttribute('seabed', this.seabed);
    this.shoreAttr = new THREE.BufferAttribute(new Float32Array(n).fill(SHORE_MAX), 1);
    this.shoreAttr.setUsage(THREE.DynamicDrawUsage);
    geom.setAttribute('shoreDist', this.shoreAttr);
    this.openAttr = new THREE.BufferAttribute(new Float32Array(n).fill(0), 1);
    this.openAttr.setUsage(THREE.DynamicDrawUsage);
    geom.setAttribute('openDist', this.openAttr);
    const pos = geom.attributes.position;
    this.localX = new Float32Array(n); this.localZ = new Float32Array(n);
    for (let k = 0; k < n; k++) { this.localX[k] = pos.getX(k); this.localZ[k] = pos.getZ(k); }

    const mat = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: true });
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.iTime = this.uTime;
      shader.uniforms.uSeaLevel = this.uSea;
      shader.uniforms.uFoamStr = this.uStr;
      shader.uniforms.uSwellAmp = this.uSwell;
      shader.uniforms.uDay = this.uDay;
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nattribute float seabed; attribute float shoreDist; attribute float openDist; uniform float uSeaLevel; uniform float iTime; uniform float uSwellAmp; varying float vDepth; varying float vFmShore; varying float vFmCalm; varying vec2 vWXZ; varying vec2 vFmLocal;\n' + swellGLSL)
        .replace('#include <begin_vertex>', `#include <begin_vertex>
 vDepth = uSeaLevel - seabed;
 vFmShore = shoreDist;    // metres to the nearest dry land (CPU chamfer over the seabed grid)
 vFmLocal = position.xz;  // plane-local position, for fading foam out before the sheet's edge
 // CALM FACTOR (ocean 0 .. enclosed lake/river 1): how far this water sits from truly OPEN water.
 vFmCalm = smoothstep(115.0, 180.0, openDist);
 // DRAPE the beach side over the sand: the sheet's origin sits just above the sea, so this lifts
 // vertices to ride ~0.17 above the seabed sample wherever the sand pokes above the water. Without it
 // the swash zone is buried under the rising terrain and never renders. Capped: never climbs cliffs.
 transformed.y += clamp(seabed + 0.08 - uSeaLevel, 0.0, 2.5);
 vec4 fwp = modelMatrix * vec4(transformed, 1.0); vWXZ = fwp.xz;
 // RIDE THE SWELL: displace by the same 3D wave height -- same radius fade AND same calm damping as
 // the water sheet -- so foam never sinks under a crest. Already ~0 wherever the drape lift is active.
 vec2 fmG;
 transformed.y += oxSwell(vWXZ, vDepth, iTime, uSwellAmp * (1.0 - smoothstep(150.0, 280.0, length(position.xz))) * mix(1.0, 0.15, vFmCalm), fmG);`);
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', '#include <common>\nuniform float iTime; uniform float uFoamStr; uniform float uDay; varying float vDepth; varying float vFmShore; varying float vFmCalm; varying vec2 vWXZ; varying vec2 vFmLocal;\n' + shoreSurgeGLSL + FOAM_NOISE_GLSL + swellGLSL)
        .replace('#include <map_fragment>', `#include <map_fragment>
          float d = vDepth; // >0 = water depth (toward sea), <0 = up the dry beach
          float t = iTime;
          float surge = oxShoreSurge(vWXZ, t); // the shared breath: 0 pulled back .. 1 washed UP the sand
          // LAND-MASS GATE: shore foam only lives near an actual coast. A shallow shelf out in the middle
          // of a bay is deep-cleaned (its nearest dry land is far), while a sandbar island keeps its ring.
          float nearLand = 1.0 - smoothstep(16.0, 42.0, vFmShore);
          // LACE FIELD: everything below gets THRESHOLDED against this scrolling 2-octave noise, so foam
          // renders as torn blobby patches full of holes -- never an airbrushed alpha gradient.
          vec2 nq = vWXZ * 1.05;
          float lace = oxFmNoise(nq + vec2(t * 0.26, -t * 0.17)) * 0.62
                     + oxFmNoise(nq * 2.9 + vec2(-t * 0.33, t * 0.41)) * 0.38;
          // WAVE FRONTS with a speed profile: the shared oxFrontA/B phases (swellGLSL -- the SAME phase
          // that displaces the water geometry, so this lace sits on the moving crests). The depth warp
          // makes crests ease in slowly from deep water, SPRINT through the mid-band, then crawl (and
          // bunch up) over the last metres of sand. Two staggered periods + wobble so arrivals never sync.
          float shoal = 1.0 - smoothstep(0.6, 5.0, d);
          float wA = oxFrontA(vWXZ, d, t);
          float wB = oxFrontB(vWXZ, d, t);
          float frontA = smoothstep(0.14, 0.02, wA) + pow(1.0 - wA, 5.0) * 0.35;
          float frontB = smoothstep(0.12, 0.02, wB) + pow(1.0 - wB, 5.0) * 0.35;
          // CALM RULE (lakes + rivers): breaking wave fronts are an OPEN-WATER thing. Enclosed water
          // keeps ~1/10th -- the odd faint line -- instead of ocean surf rolling onto a pond shore.
          float fronts = (frontA * 0.62 + frontB * 0.45) * shoal * smoothstep(-0.05, 0.25, d) * mix(1.0, 0.1, vFmCalm);
          // SWASH: the surge-driven lick running up the sand (brightest at the moving wet edge), plus a
          // thin contact line pinned to the waterline so the seam never reads bare between waves.
          float reach = 0.15 + surge * 0.9;
          float swash = smoothstep(-reach, 0.10, d) * (1.0 - smoothstep(0.10, 0.8, d));
          float lick = 1.0 - smoothstep(0.0, 0.4, abs(-d - surge * 0.8 + 0.15));
          // CALM RULE: lakes lap gently -- the surge lick drops to ~45% strength. The thin contact
          // line stays at full strength everywhere (that part of the lake look was already right).
          float beach = swash * (0.28 + 0.72 * lick) * (0.30 + 0.70 * surge) * mix(1.0, 0.45, vFmCalm);
          float contact = (1.0 - smoothstep(0.03, 0.25, abs(d))) * 0.35;
          // Foam amount -> lace threshold: solid-but-torn where strong, scattered dissolving flecks
          // where weak, and a floor gate so quiet water stays perfectly clean.
          float m = clamp((fronts + beach + contact) * nearLand, 0.0, 1.0);
          float thr = 1.0 - m * 0.78;
          float a = smoothstep(thr - 0.14, thr + 0.14, lace) * (0.55 + 0.45 * m);
          a *= smoothstep(0.04, 0.12, m);
          // OPEN-WATER FLECKS: patches that MORPH instead of merely drifting. The base field crossfades
          // between two hash-offset noise samples on a ~1.2s clock, so clumps genuinely appear and dissolve
          // in random spots; ~25m cells run that clock at their own phase so the sea never pulses in unison.
          // Not land-gated -- these live everywhere on the water.
          float fph = t * 0.85 + oxFmHash(floor(vWXZ * 0.04)) * 7.0;
          vec2 fq = vWXZ * 1.15 + vec2(t * 0.35, -t * 0.22);
          float f0 = floor(fph), ff = smoothstep(0.0, 1.0, fract(fph));
          float fine = mix(oxFmNoise(fq + vec2(f0 * 19.0, f0 * 7.0)),
                           oxFmNoise(fq + vec2((f0 + 1.0) * 19.0, (f0 + 1.0) * 7.0)), ff);
          float fleckN = fine * 0.62 + oxFmNoise(vWXZ * 3.1 + vec2(-t * 0.45, t * 0.30)) * 0.38;
          float drift = oxFmNoise(vWXZ * 0.045 + vec2(t * 0.07, -t * 0.05));
          // CALM RULE: fewer whitecap flecks on still lake water (wind chop is an open-water thing).
          float flecks = smoothstep(0.55, 0.68, fleckN * (0.55 + 0.45 * drift)) * smoothstep(0.35, 1.2, d) * mix(1.0, 0.45, vFmCalm);
          a = max(a, flecks * 0.38);
          // The sheet ends 190m out -- fade everything well before the border so foam never pops there.
          a *= 1.0 - smoothstep(120.0, 178.0, length(vFmLocal));
          // Dim the foam with the day-night cycle: bright white by day, a dark blue-grey wash at night so the
          // shore ring doesn't glow like daylight after dark (matches the near-black night ground/water).
          diffuseColor.rgb = vec3(0.96, 0.985, 1.0) * mix(0.06, 1.0, clamp(uDay, 0.0, 1.0));
          diffuseColor.a = clamp(a, 0.0, 1.0) * uFoamStr;`);
    };
    mat.customProgramCacheKey = () => 'shore-foam7-calm';
    this.mesh = new THREE.Mesh(geom, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 2; // above the water sheet (detail water is 1, far water 0)
    // +0.09 clearance: foam and water tessellate the swell differently (3.96m vs 5m quads), so mid-quad
    // interpolation can sag a few cm -- the old +0.06 let crests poke through the sheet.
    this.mesh.position.y = TERRAIN.seaLevel + 0.09;
  }

  setStrength(s: number): void { this.uStr.value = s; }
  setDay(v: number): void { this.uDay.value = v; } // 0 night .. 1 day: drives the foam night-dim

  // Recenter on the focus + refresh the seabed sample (only when the plane actually moved), like the water.
  update(focusX: number, focusZ: number): void {
    this.mesh.position.x = focusX; this.mesh.position.z = focusZ;
    this.mesh.position.y = TERRAIN.seaLevel + 0.09;
    this.uSea.value = TERRAIN.seaLevel;
    this.uSwell.value = WATER.swellAmp; // track the live F2 knob, in lockstep with the water sheet
    const dx = focusX - this.lastX, dz = focusZ - this.lastZ;
    if (!(dx * dx + dz * dz < 0.04)) { this.lastX = focusX; this.lastZ = focusZ; this.refreshSeabed(focusX, focusZ); }
  }

  private refreshSeabed(fx: number, fz: number): void {
    const half = FOAM_SIZE / 2, hm = this.hm, sd = this.sd;
    const gx0 = Math.floor((fx - half) / GRID_STEP) * GRID_STEP;
    const gz0 = Math.floor((fz - half) / GRID_STEP) * GRID_STEP;
    if (gx0 !== this.gx0 || gz0 !== this.gz0) {
      this.gx0 = gx0; this.gz0 = gz0;
      let minH = Infinity, maxH = -Infinity;
      for (let j = 0; j < GP; j++) for (let i = 0; i < GP; i++) {
        const h = terrainHeight(gx0 + i * GRID_STEP, gz0 + j * GRID_STEP);
        hm[j * GP + i] = h; if (h < minH) minH = h; if (h > maxH) maxH = h;
      }
      // Draw whenever there is any water in the window (the open-water flecks live everywhere on the
      // sea, not just where the plane straddles a shoreline). Fully-inland windows still skip it.
      this.mesh.visible = minH < TERRAIN.seaLevel + 0.5;
      // LAND-MASS DETECTOR: metres from every grid point to the nearest DRY LAND cell (two-pass 8-way
      // chamfer over the same heightmap -- ~2x 2.6k cells, only when the grid shifts). The shader fades
      // shore foam out past ~40m from a real coast, so a shallow shelf mid-bay stops reading as surf.
      const sea = TERRAIN.seaLevel;
      for (let k = 0; k < sd.length; k++) sd[k] = hm[k] > sea + 0.05 ? 0 : 1e9;
      chamferRelax(sd, GP, GRID_STEP);
      // OPEN-WATER DETECTOR (ocean vs lake vs river): distance to the nearest cell that is itself
      // >=95m from any land. Enclosed lakes and rivers never contain such a cell, so they max out ->
      // CALM water (tiny lapping, no surf). Ocean coasts sit ~95-130m -> full waves; deep sheltered
      // bays land in between and calm down accordingly. The shaders read this as vFmCalm.
      const od = this.od;
      for (let k = 0; k < od.length; k++) od[k] = sd[k] >= 95 ? 0 : 1e9;
      chamferRelax(od, GP, GRID_STEP);
    }
    const arr = this.seabed.array as Float32Array;
    const shr = this.shoreAttr.array as Float32Array;
    const opn = this.openAttr.array as Float32Array;
    const od = this.od;
    const lx = this.localX, lz = this.localZ, inv = 1 / GRID_STEP;
    for (let k = 0; k < arr.length; k++) {
      const u = (fx + lx[k] - gx0) * inv, v = (fz + lz[k] - gz0) * inv;
      let i = u | 0, j = v | 0;
      if (i < 0) i = 0; else if (i >= GW) i = GW - 1;
      if (j < 0) j = 0; else if (j >= GW) j = GW - 1;
      const tx = u - i, tz = v - j, b = j * GP + i;
      const w00 = (1 - tx) * (1 - tz), w10 = tx * (1 - tz), w01 = (1 - tx) * tz, w11 = tx * tz;
      arr[k] = hm[b] * w00 + hm[b + 1] * w10 + hm[b + GP] * w01 + hm[b + GP + 1] * w11;
      const s = sd[b] * w00 + sd[b + 1] * w10 + sd[b + GP] * w01 + sd[b + GP + 1] * w11;
      shr[k] = s > SHORE_MAX ? SHORE_MAX : s; // no-land-in-window cells sit at 1e9 -> clamp
      const o = od[b] * w00 + od[b + 1] * w10 + od[b + GP] * w01 + od[b + GP + 1] * w11;
      opn[k] = o > 240 ? 240 : o; // fully-enclosed water has no open cells at all -> clamp = full calm
    }
    this.seabed.needsUpdate = true;
    this.shoreAttr.needsUpdate = true;
    this.openAttr.needsUpdate = true;
  }
}
